/**
 * The "needs you now" panel at the top of /team.
 *
 * A queue, not a dashboard: the server orders it (blocking questions oldest
 * first, then normal pending questions, then reviews and briefings, then
 * stuck/offline employees, then recent failures) and every row deep-links to
 * the place where the decision is made. The empty state is the payoff — "Nothing needs you right now" is
 * the sentence the whole redesign exists to make trustworthy.
 */
import { useState } from "react";
import { Link } from "react-router-dom";

import { adminApi, type AiTeamAttention, type AiTeamAttentionItem } from "../../api/admin";
import { Age } from "../../components/Age";
import { Pill } from "../../components/Pill";
import { PriorityBadge } from "../../components/PriorityBadge";
import { QueryBoundary } from "../../components/QueryBoundary";
import { Skeleton } from "../../components/Skeleton";
import {
  agentDrawerUrl,
  questionAgeTone,
  reviewUrl,
  toneTextClass,
  urgencyTone,
} from "../../lib/aiTeam";
import { meetingLabel } from "../../lib/briefings";

export function TriagePanel() {
  const attentionQuery = adminApi.aiTeam.attention();
  const data = attentionQuery.data;

  // The heading always renders: "Nothing needs you right now" must only ever
  // be said on real data, never stand in for a load that is still running or
  // has failed.
  return (
    <section className="panel mb-5">
      <div className="flex items-center justify-between gap-3 mb-2">
        <h2 className="font-heading font-semibold text-ink">Needs you</h2>
        {data && data.items.length > 0 ? (
          <span className="text-xs text-ink-low tnum">{countsLine(data.counts)}</span>
        ) : null}
      </div>
      <QueryBoundary
        query={attentionQuery}
        loading={
          <div className="space-y-2 py-1" aria-busy="true">
            <Skeleton height={18} width="70%" />
            <Skeleton height={18} width="55%" />
            <Skeleton height={18} width="62%" />
          </div>
        }
      >
        {(d) =>
          d.items.length === 0 ? (
            <div className="text-sm text-ink-low py-2">Nothing needs you right now.</div>
          ) : (
            <ul className="divide-y divide-rule">
              {d.items.map((item) => (
                <TriageRow key={`${item.kind}-${item.id}`} item={item} />
              ))}
            </ul>
          )
        }
      </QueryBoundary>
    </section>
  );
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

function countsLine(c: AiTeamAttention["counts"]): string {
  const parts = [plural(c.pending_questions, "question", "questions")];
  if (c.pending_reviews > 0) parts.push(plural(c.pending_reviews, "review", "reviews"));
  if ((c.pending_email_drafts ?? 0) > 0) {
    parts.push(plural(c.pending_email_drafts ?? 0, "email draft", "email drafts"));
  }
  if ((c.pending_briefings ?? 0) > 0) {
    parts.push(plural(c.pending_briefings ?? 0, "briefing", "briefings"));
  }
  if (c.agents_alerting > 0) parts.push(plural(c.agents_alerting, "employee alert", "employee alerts"));
  if (c.recent_failures > 0) parts.push(plural(c.recent_failures, "failure", "failures"));
  return parts.join(" · ");
}

function triageLink(item: AiTeamAttentionItem): string | null {
  if (item.kind === "review") {
    return reviewUrl(item.review_id ?? item.id, item.review_kind);
  }
  if (!item.agent_id) return null;
  if (item.kind === "question") {
    return item.job_id
      ? agentDrawerUrl(item.agent_id, { tab: "jobs", jobId: item.job_id })
      : agentDrawerUrl(item.agent_id, { tab: "questions" });
  }
  if (item.kind === "job_failure") {
    return item.job_id
      ? agentDrawerUrl(item.agent_id, { tab: "jobs", jobId: item.job_id })
      : agentDrawerUrl(item.agent_id, { tab: "jobs" });
  }
  return agentDrawerUrl(item.agent_id);
}

const KIND_LABEL: Record<AiTeamAttentionItem["kind"], string> = {
  question: "question",
  review: "review",
  agent: "employee",
  job_failure: "failed",
};

function TriageRow({ item }: { item: AiTeamAttentionItem }) {
  // Age tone escalates like the question cards' — a triage row that has been
  // sitting for a day should not read as calmly as one from ten minutes ago.
  const [nowMs] = useState(() => Date.now());
  const createdIso = new Date(nowMs - item.age_s * 1000).toISOString();
  const to = triageLink(item);
  const briefing = item.kind === "review" && item.review_kind === "briefing";

  const body = (
    <div className="flex items-center gap-3 py-2">
      <Pill
        value={
          item.kind === "question"
            ? item.urgency === "blocking"
              ? "blocking"
              : "question"
            : item.kind === "review" && item.review_kind === "email"
              ? "email draft"
              : briefing
                ? "briefing"
                : KIND_LABEL[item.kind]
        }
        tone={
          item.kind === "question"
            ? urgencyTone(item.urgency)
            : item.kind === "agent"
              ? "alert"
              : "signal"
        }
      />
      {item.returned ? <Pill value="back" tone="signal" /> : null}
      {item.kind === "review" && item.priority != null && !briefing ? <PriorityBadge priority={item.priority} /> : null}
      {briefing ? (
        <>
          <span className="font-mono text-xs text-ink-low tnum shrink-0">{meetingLabel(item, nowMs)}</span>
          {/* What the owner owes: the debrief after the meeting, a read before it. */}
          <span className={`text-xs shrink-0 ${toneTextClass(item.needs === "debrief" ? "signal" : "muted")}`}>
            {item.needs === "debrief" ? "debrief" : "read"}
          </span>
        </>
      ) : null}
      {item.topic ? <span className="text-xs text-ink-low shrink-0">{item.topic.name}</span> : null}
      <span className="min-w-0 flex-1 text-sm text-ink truncate">
        {item.agent_slug ? (
          <span className="font-mono text-xs text-ink-low mr-2">
            {item.agent_slug}
          </span>
        ) : null}
        {/* The server prefixes review titles with "P<n> · "; the badge says it already. */}
        {item.kind === "review" && item.priority != null ? item.title.replace(/^P\d+ · /, "") : item.title}
      </span>
      <Age
        seconds={item.age_s}
        tone={item.kind === "question" ? questionAgeTone(createdIso, nowMs) : "muted"}
      />
    </div>
  );

  return (
    <li>
      {to ? (
        <Link className="block hover:bg-surface-tint" to={to}>
          {body}
        </Link>
      ) : (
        body
      )}
    </li>
  );
}
